/**
 * Images shown as figures, in the editor and in previews: `![Caption](bl://file/<id>)` of an image
 * file. The bytes are fetched once per page (the server sends files uncached), checked to really be
 * a PNG, JPEG, WebP or GIF image, and shown from memory. End-to-end encrypted ones are decrypted
 * here, in this tab only.
 */
import { detectImageType } from "@brainlog/shared";
import { ApiError, errorMessage, get } from "@/lib/api";
import { LockedError, fetchDecryptedFile } from "@/features/crypto/ops";

export class FigureError extends Error {}

const cache = new Map<string, Promise<string>>();

async function load(id: string, workspaceId?: string): Promise<string> {
  let file: { encrypted: boolean; encMeta: string | null; workspaceId: string };
  try {
    file = (await get<{ file: typeof file }>(`/api/files/${id}`)).file;
  } catch (e) {
    throw new FigureError(e instanceof ApiError && e.status === 404 ? "This image is not available: its file was deleted, or you cannot open it." : errorMessage(e));
  }
  if (workspaceId && file.workspaceId !== workspaceId) throw new FigureError("This image is a file of another notebook or project. Add the figure again from this project.");
  let bytes: Uint8Array;
  if (file.encrypted) {
    try {
      bytes = (await fetchDecryptedFile({ id, encMeta: file.encMeta })).bytes;
    } catch (e) {
      throw new FigureError(e instanceof LockedError ? "Unlock the project to see this figure." : "This image could not be decrypted.");
    }
  } else {
    const res = await fetch(`/api/attachments/${id}/file`, { credentials: "same-origin" });
    if (!res.ok) throw new FigureError("This image could not be loaded. Try again in a moment.");
    bytes = new Uint8Array(await res.arrayBuffer());
  }
  const type = detectImageType(bytes);
  if (!type) throw new FigureError("This file is not a PNG, JPEG, WebP or GIF image.");
  return URL.createObjectURL(new Blob([bytes as BlobPart], { type: type.mime }));
}

/**
 * A URL that shows the figure's image (kept in memory for this page). With a workspace, the file
 * must belong to it. Fails with a FigureError that says why, in plain words.
 */
export function figureImageUrl(id: string, workspaceId?: string): Promise<string> {
  const key = `${id.toLowerCase()}:${workspaceId ?? ""}`;
  let p = cache.get(key);
  if (!p) {
    p = load(id.toLowerCase(), workspaceId);
    cache.set(key, p);
    // A failure is tried again next time (the project unlocked, the connection back).
    p.catch(() => cache.delete(key));
  }
  return p;
}
